import {
  CheckCircleFilled,
  CodeOutlined,
  FileDoneOutlined,
  GithubOutlined,
  RobotOutlined,
  SwapOutlined,
} from '@ant-design/icons';
import React, { useEffect, useMemo, useState } from 'react';

type RoadmapStatus = 'complete' | 'running' | 'pending' | 'ready' | 'error';

interface ReviewExecutionRoadmapProps {
  loading: boolean;
  exportedFiles?: number;
  changeCount?: number;
  evidenceDigest?: string;
  reviewComplete?: boolean;
  conflictCount?: number;
  targetLabel: string;
  mode?: 'commit' | 'checkout';
}

interface RoadmapStep {
  title: string;
  purpose: string;
  detail: string;
  status: RoadmapStatus;
  icon: React.ReactNode;
}

const formatElapsed = (seconds: number) => `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;

export const ReviewExecutionRoadmap: React.FC<ReviewExecutionRoadmapProps> = ({
  loading,
  exportedFiles,
  changeCount,
  evidenceDigest,
  reviewComplete,
  conflictCount = 0,
  targetLabel,
  mode = 'commit',
}) => {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!loading) return;
    const timer = window.setInterval(() => setElapsed(value => value + 1), 1000);
    return () => window.clearInterval(timer);
  }, [loading]);

  const steps = useMemo<RoadmapStep[]>(() => {
    if (loading) {
      return [
        {
          title: 'Export Qlik App',
          purpose: `Read the controlled ${targetLabel} into versionable Qlik objects.`,
          detail: `Server task in progress · elapsed ${formatElapsed(elapsed)}`,
          status: 'running',
          icon: <FileDoneOutlined />,
        },
        { title: 'Canonicalize JSON', purpose: 'Remove runtime noise and normalize stable properties.', detail: 'Waiting for export evidence', status: 'pending', icon: <CodeOutlined /> },
        { title: 'Compare with Git', purpose: 'Compare canonical Qlik objects with the current Git content.', detail: 'Waiting for canonical project', status: 'pending', icon: <SwapOutlined /> },
        { title: 'AI interpret changes', purpose: 'Turn property evidence into a short business narrative.', detail: 'Waiting for detected changes', status: 'pending', icon: <RobotOutlined /> },
        { title: mode === 'checkout' ? 'Check out latest revision' : 'Commit to GitHub', purpose: mode === 'checkout' ? 'Restore reviewed Git evidence into a disposable Preview App.' : 'Commit only reviewed evidence to the mapped branch.', detail: mode === 'checkout' ? 'Waiting for incoming comparison' : 'Locked until review completes', status: 'pending', icon: <GithubOutlined /> },
      ];
    }

    const commitStatus: RoadmapStatus = conflictCount > 0 ? 'error' : reviewComplete ? 'ready' : 'pending';
    return [
      { title: 'Export Qlik App', purpose: `Read the controlled ${targetLabel} into versionable Qlik objects.`, detail: `${exportedFiles ?? 0} JSON files exported`, status: 'complete', icon: <FileDoneOutlined /> },
      { title: 'Canonicalize JSON', purpose: 'Remove runtime noise and normalize stable properties.', detail: evidenceDigest ? `Evidence ${evidenceDigest.slice(0, 12)}` : 'Canonical evidence created', status: 'complete', icon: <CodeOutlined /> },
      { title: 'Compare with Git', purpose: 'Compare canonical Qlik objects with the current Git content.', detail: 'Comparison complete', status: 'complete', icon: <SwapOutlined /> },
      { title: 'AI interpret changes', purpose: 'Turn property evidence into a short business narrative.', detail: `${changeCount ?? 0} change${changeCount === 1 ? '' : 's'} interpreted`, status: 'complete', icon: <RobotOutlined /> },
      {
        title: mode === 'checkout' ? 'Check out latest revision' : 'Commit to GitHub',
        purpose: mode === 'checkout' ? 'Restore reviewed Git evidence into a disposable Preview App.' : 'Commit only reviewed evidence to the mapped branch.',
        detail: conflictCount > 0 ? `${conflictCount} conflict${conflictCount === 1 ? '' : 's'} must be resolved` : reviewComplete ? mode === 'checkout' ? 'Ready to create Preview' : 'Ready to commit' : 'Pending business review',
        status: commitStatus,
        icon: <GithubOutlined />,
      },
    ];
  }, [changeCount, conflictCount, elapsed, evidenceDigest, exportedFiles, loading, mode, reviewComplete, targetLabel]);

  return (
    <aside className="review-roadmap" aria-label="Review execution roadmap">
      <div className="roadmap-heading"><strong>Review execution</strong><span>{loading ? formatElapsed(elapsed) : 'Evidence ready'}</span></div>
      <ol>
        {steps.map((step, index) => (
          <li className={`roadmap-step is-${step.status}`} key={step.title}>
            <span className="roadmap-icon">{step.icon}</span>
            <div className="roadmap-step-copy">
              <span className="roadmap-step-number">{index + 1}</span>
              <strong>{step.title}</strong>
              <span className="roadmap-status">{step.status === 'complete' ? 'Completed' : step.status === 'running' ? 'Running' : step.status === 'ready' ? 'Ready' : step.status === 'error' ? 'Blocked' : 'Pending'}</span>
              <p>{step.purpose}</p>
              <small>{step.detail}</small>
              {step.status === 'running' && <span className="roadmap-live"><span /> Waiting for server confirmation</span>}
            </div>
          </li>
        ))}
      </ol>
      <div className="roadmap-trust"><CheckCircleFilled /> Source App remains unchanged</div>
    </aside>
  );
};
